/**
 * Medium filter for the meter catalog section.
 *
 * The cards are all rendered server side from a collection; this element only
 * shows and hides them, so adding a product to the collection is enough to make
 * it appear here and in the chip counts. Without JavaScript every card stays
 * visible and the chip row is hidden by CSS (`:not(:defined)`).
 *
 * Reads and writes the `?medium=<slug>` query parameter so a menu entry can link
 * straight to a pre-filtered catalog.
 */
class MeterCatalog extends HTMLElement {
  static #param = 'medium';

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {HTMLButtonElement[]} */
  get #chips() {
    return Array.from(this.querySelectorAll('button[data-filter]'));
  }

  /** @type {HTMLElement[]} */
  get #cards() {
    return Array.from(this.querySelectorAll('[data-group]'));
  }

  connectedCallback() {
    // Fresh controller per connect: the previous one was aborted on disconnect, and
    // addEventListener with an aborted signal silently attaches nothing — the chips
    // would come back as dead buttons with cards stuck hidden.
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', this.#handleClick, { signal });

    const fromUrl = new URLSearchParams(window.location.search).get(MeterCatalog.#param);
    const known = fromUrl && this.#chips.some((chip) => chip.dataset.filter === fromUrl);
    this.#apply(known && fromUrl ? fromUrl : this.#activeFilter, { updateUrl: false });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get #activeFilter() {
    const pressed = this.#chips.find((chip) => chip.getAttribute('aria-pressed') === 'true');
    return pressed?.dataset.filter ?? 'all';
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const chip = target.closest('button[data-filter]');
    if (!(chip instanceof HTMLButtonElement)) return;

    this.#apply(chip.dataset.filter ?? 'all', { updateUrl: true });
  };

  /**
   * @param {string} filter - Medium slug, or `all`.
   * @param {{updateUrl: boolean}} options
   */
  #apply(filter, options) {
    for (const chip of this.#chips) {
      chip.setAttribute('aria-pressed', String(chip.dataset.filter === filter));
    }

    let visible = 0;
    for (const card of this.#cards) {
      const matches = filter === 'all' || card.dataset.group === filter;
      card.hidden = !matches;
      if (matches) visible++;
    }

    // Only after a tap: announcing on page load would read the count out unprompted.
    if (!options.updateUrl) return;

    this.#announce(visible);

    // History replace, not push: filtering is not a navigation step people expect
    // the back button to walk through.
    const url = new URL(window.location.href);
    if (filter === 'all') url.searchParams.delete(MeterCatalog.#param);
    else url.searchParams.set(MeterCatalog.#param, filter);
    window.history.replaceState({}, '', url);
  }

  /** @param {number} count */
  #announce(count) {
    const status = this.querySelector('[data-status]');
    if (!(status instanceof HTMLElement)) return;

    const template = (count === 1 ? this.dataset.statusOne : this.dataset.statusMany) ?? '';
    status.textContent = template.replace('{count}', String(count));
  }
}

if (!customElements.get('meter-catalog')) {
  customElements.define('meter-catalog', MeterCatalog);
}
